import create from 'zustand'
import { persist } from 'zustand/middleware'
import { devtools } from 'zustand/middleware'
import { Notification, Feedback } from '@/services/UOPService'
import { closest } from '../utilities/contents'
import { searchKeywordType } from '../utilities/contents'

export type Store = {
    infoMode: boolean
    notifications: Notification[]
    feedbackList: Feedback[]
    setInfoMode: (infoMode: boolean) => void
    setNotifications: (notifications: Notification[]) => void
    setFeedbackList: (feedback: Feedback[]) => void
    addNotification: (notification: Notification) => void
    removeNotification: (id: string) => void
    markReadNotification: (id: string) => void
    markUnReadNotification: (id: string) => void
    getUnreadNotifications: () => Notification[]
}

export type SearchKeywordStore = {
    searchKeywords: searchKeywordType[]
    addKeyword: (keyword: string, searchText: string) => void
}

const initialSearchKeywords: any[] = []

const initialNotifications: Notification[] = []

export type NavHistoryStore = {
    forwardCount: number
    backwardCount: number
    setForwardCount: (n: number) => void
    setBackwardCount: (n: number) => void
    updateForwardState: () => void
    updateBackwardState: () => void
    updatePushState: () => void
}

export type NPSFilters = {
    fromDate: string
    toDate: string
    nameEmail: string
    comment: string
}

export type NPSFiltersStore = {
    filters: NPSFilters
    updateFilter: (filters: NPSFilters) => void
}

export type ApplicationMessage = {
    id: string
    messageType: 'success' | 'error' | 'info'
    message: string | undefined
}
export type ApplicationShellStore = {
    messages: ApplicationMessage[]
    addMessage: (message: ApplicationMessage) => void
    setMessages: (messages: ApplicationMessage[]) => void
    removeMessage: (id: string) => void
}

export const useApplicationShellStore = create<ApplicationShellStore>(
    devtools((set, get) => ({
        messages: [],
        setMessages: (messages: ApplicationMessage[]) => {
            set(() => ({
                messages,
            }))
        },
        addMessage: (message: ApplicationMessage) => {
            set(state => ({
                messages: [message, ...state.messages],
            }))
        },
        removeMessage: (id: string) => {
            set(state => ({
                messages: [...state.messages.filter(item => item.id !== id)],
            }))
        },
    })),
)

// Application User Configurable Settings
export type ApplicationPreferencesStore = {
    showTourOnStartup: boolean
    themeSelection: boolean // if true then dark and light mode is enabled, else only light mode is enabled
    pageFeedbackExpanded: boolean // if true then page feedback widget is expanded by default
    currentAppViewed: string | undefined // last app viewed by user
    setShowTourOnStartup: (showTourOnStartup: boolean) => void
    setThemeSelection: (themeSelection: boolean) => void
    setPageFeedbackExpanded: (pageFeedbackExpanded: boolean) => void
    setCurrentAppViewed: (currentAppViewed: string | undefined) => void
}

export const useApplicationPreferencesStore =
    create<ApplicationPreferencesStore>(
        devtools(
            persist(
                set => ({
                    themeSelection: false,
                    pageFeedbackExpanded: false,
                    currentAppViewed: undefined,
                    showTourOnStartup: true,
                    setShowTourOnStartup: (showTourOnStartup: boolean) => {
                        set(() => ({
                            showTourOnStartup,
                        }))
                    },
                    setThemeSelection: (themeSelection: boolean) => {
                        set(() => ({
                            themeSelection,
                        }))
                    },
                    setPageFeedbackExpanded: (
                        pageFeedbackExpanded: boolean,
                    ) => {
                        set(() => ({
                            pageFeedbackExpanded,
                        }))
                    },
                    setCurrentAppViewed: (
                        currentAppViewed: string | undefined,
                    ) => {
                        set(() => ({
                            currentAppViewed,
                        }))
                    },
                }),
                {
                    name: 'portal-preferences',
                },
            ),
        ),
    )
export const useSearchKeywordsStore = create<SearchKeywordStore>(
    devtools(
        persist(
            (set, get) => ({
                searchKeywords: initialSearchKeywords,
                addKeyword: (keyword: string, searchText: string) => {
                    // new code
                    const index = closest(keyword, get().searchKeywords)
                    if (index === -1) {
                        //set data no closest match found
                        set(state => ({
                            searchKeywords: [
                                {
                                    displayText: keyword,
                                    searchText: searchText,
                                },
                                ...state.searchKeywords.slice(0, 4),
                            ],
                        }))
                    } else if (index > -1) {
                        //overwrite the matched one with new text on top
                        const edited = get().searchKeywords
                        edited.splice(index, 1)
                        set(state => ({
                            searchKeywords: [
                                {
                                    displayText: keyword,
                                    searchText: searchText,
                                },
                                ...edited,
                            ],
                        }))
                    } else {
                        // do nothing if same string exists
                    }
                    //new code
                },
            }),
            {
                name: 'konnect-searchKeywords-v2',
            },
        ),
    ),
)

export const useStore = create<Store>(
    devtools((set, get) => ({
        notifications: initialNotifications,
        feedbackList: [],
        infoMode: false,
        setInfoMode: (infoMode: boolean) => {
            set(() => ({
                infoMode,
            }))
        },
        setFeedbackList: (feedback: Feedback[]) => {
            set({
                feedbackList: feedback,
            })
        },
        setNotifications: (notifications: Notification[]) => {
            set({ notifications })
        },
        addNotification(notification: Notification) {
            set(state => ({
                ...state,
                notifications: [
                    ...state.notifications,
                    {
                        ...notification,
                        read: false,
                        eventTime: new Date().toString(),
                    },
                ],
            }))
        },
        removeNotification(id: string) {
            set(state => ({
                ...state,
                notifications: state.notifications.filter(n => n.id === id),
            }))
        },
        markReadNotification(id: string) {
            set(state => ({
                ...state,
                notifications: updateNotificationAsRead(
                    state.notifications || ([] as Notification[]),
                    id,
                ),
            }))
        },
        markUnReadNotification(id: string) {
            set(state => ({
                ...state,
                notifications: updateNotificationAsUnRead(
                    state.notifications || ([] as Notification[]),
                    id,
                ),
            }))
        },
        getUnreadNotifications() {
            const notifications = get().notifications
            return notifications && notifications.length > 0
                ? notifications.filter(n => n.read === false)
                : []
        },
    })),
)

function updateNotificationAsRead(
    notifications: Notification[],
    id: string,
): Notification[] {
    return notifications.map(n => {
        if (n.id === id) {
            n.read = true
        }
        return n
    })
}

function updateNotificationAsUnRead(
    notifications: Notification[],
    id: string,
): Notification[] {
    return notifications.map(n => {
        if (n.id === id) {
            n.read = false
        }
        return n
    })
}

export const useNavHistory = create<NavHistoryStore>(
    devtools((set, get) => ({
        forwardCount: 0,
        backwardCount: 0,
        setBackwardCount: n => {
            set({ backwardCount: n })
        },
        setForwardCount: n => {
            set({ forwardCount: n })
        },
        updatePushState: () => {
            const back = get().backwardCount + 1
            set({ backwardCount: back, forwardCount: 0 })
        },
        updateForwardState: () => {
            const back = get().backwardCount + 1
            const forward = get().forwardCount > 0 ? get().forwardCount - 1 : 0
            set({ backwardCount: back, forwardCount: forward })
        },
        updateBackwardState: () => {
            const back = get().backwardCount > 0 ? get().backwardCount - 1 : 0
            const forward = get().forwardCount + 1
            set({ backwardCount: back, forwardCount: forward })
        },
    })),
)

export const useNpsFilter = create<NPSFiltersStore>(
    devtools((set, get) => ({
        filters: {
            fromDate: '',
            toDate: '',
            nameEmail: '',
            comment: '',
        },
        updateFilter: (filters: NPSFilters) => {
            set({ filters })
        },
    })),
)

export type ResourceHistoryStore = {
    appList: any[]
    resourceList: any[]
    searchHistory: any[]
    setApplicationList: (list: any[]) => void
    setResourceList: (list: any[]) => void
    setSearchHistory: (list: any[]) => void
}

export const useResourceHistory = create<ResourceHistoryStore>(
    devtools((set, get) => ({
        appList: [],
        resourceList: [],
        searchHistory: [],
        setApplicationList: (appList: any[]) => {
            set({ appList })
        },
        setResourceList: (resourceList: any[]) => {
            set({ resourceList })
        },
        setSearchHistory: (searchHistory: any[]) => {
            set({ searchHistory })
        },
    })),
)
